import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcBillingV1ChargeQueryServiceChargeGet } from '@interxfi/api-client/generated/hooks';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { DEFAULT_WRAPPING_LAYOUT_COLUMN, LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container';
import { WrappingLayoutContainer } from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcBillingV1ChargeState } from '@interxfi/types';
import { BillingChargeEntityMenu } from '../../../../../../features/billing/charge/billing-charge-entity-menu';
import { BillingChargeEventSummary } from '../../../../../../features/billing/charge/billing-charge-event';
import { getBillingChargeStateListItems } from '../../../../../../features/billing/charge/billing-charge-list-items';
import { getBillingChargeDetailPath } from '../../../../../../features/billing/charge/billing-charge-routing';
import { BillingChargeStatusPill } from '../../../../../../features/billing/charge/billing-charge-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/billing/charge/_charge/$chargeId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `billing-charge-${params.chargeId}`,
        label: 'Charge',
        to: getBillingChargeDetailPath(params.chargeId),
        suffix: <CopyButton content={params.chargeId} label="Copy Charge ID" tooltip={{ copy: params.chargeId, copied: 'Copied Charge ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Billing Charge'),
  component: AccountingManualChargeDetail,
});

function getListItems(data: IxcBillingV1ChargeState | undefined) {
  const items = getBillingChargeStateListItems(data);

  return {
    left: [
      items.data.spec.amount,
      items.data.spec.description,
      items.data.spec.category,
      { ...items.data.contra.accountId, title: 'Contra Account ID' },
      items.data.customerTransactionId,
      items.data.correspondentTransactionId,
    ] as const satisfies ReadonlyListItem[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.chargeId,
      items.accountId,
      items.data.holdId,
    ] as const satisfies ReadonlyListItem[],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load charge', 'view this charge');

function AccountingManualChargeDetail() {
  const { chargeId } = Route.useParams();
  const request = { chargeId } as const;
  const { data, error, isPending } = useIxcBillingV1ChargeQueryServiceChargeGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.charge), [data?.charge]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
            <BillingChargeStatusPill isPending={showPending} size="large" status={data?.charge?.status} />
          </HorizontalStack>

          <BillingChargeEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <BillingChargeEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
